import type { Database } from "@/integrations/supabase/types";

export type Pick = Database["public"]["Tables"]["picks"]["Row"];
export type ScoreBreakdown = Database["public"]["Tables"]["pick_score_breakdowns"]["Row"];

export const SPORT_META: Record<string, { label: string; color: string; icon: string }> = {
  NBA: { label: "NBA", color: "bg-orange-500/15 text-orange-400 border-orange-500/30", icon: "🏀" },
  NFL: { label: "NFL", color: "bg-amber-700/15 text-amber-500 border-amber-600/30", icon: "🏈" },
  MLB: { label: "MLB", color: "bg-blue-500/15 text-blue-400 border-blue-500/30", icon: "⚾" },
  NHL: { label: "NHL", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30", icon: "🏒" },
  SOCCER: { label: "Soccer", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", icon: "⚽" },
};

export const BET_LABEL: Record<string, string> = {
  player_prop: "Player Prop",
  moneyline: "Moneyline",
  spread: "Spread",
  game_total: "Game Total",
  team_total: "Team Total",
  parlay: "Parlay",
};

export const RISK_COLOR: Record<string, string> = {
  low: "bg-success/15 text-success border-success/30",
  medium: "bg-warning/15 text-warning border-warning/30",
  high: "bg-danger/15 text-danger border-danger/30",
};

export function formatOdds(o?: number | null) {
  if (o == null) return "—";
  return o > 0 ? `+${o}` : `${o}`;
}

export function scoreToUnits(score: number): number {
  if (score >= 90) return 5;
  if (score >= 80) return 4;
  if (score >= 70) return 3;
  if (score >= 60) return 2;
  if (score >= 50) return 1;
  return 0;
}

/**
 * The "betting day" is anchored to America/Los_Angeles (Pacific Time)
 * with a 3:00 AM PT rollover so late NBA/NHL games settle on their
 * original game day. Implementation lives in `@/lib/timezone`.
 */
export { BETTING_TZ, DISPLAY_TZ, TZ_LABEL, formatPT, formatPTDate, todayBettingDatePT, nextBettingDatePT } from "./timezone";
import { bettingDayBoundsPT } from "./timezone";

/** Returns ISO start/end timestamps for the current betting day (PT, 3am rollover). */
export function bettingDayBounds(now = new Date()) {
  return bettingDayBoundsPT(now);
}

/**
 * Returns bounds for the most recent betting day that has at least one published pick,
 * falling back up to `lookbackDays` days. Defaults to today if today already has picks.
 */
export async function activeBettingDayBounds(
  supabase: { from: (t: string) => any },
  opts: { top5Only?: boolean; lookbackDays?: number } = {}
) {
  const lookback = opts.lookbackDays ?? 7;
  for (let i = 0; i <= lookback; i++) {
    const ref = new Date();
    ref.setDate(ref.getDate() - i);
    const bounds = bettingDayBounds(ref);
    let q = supabase.from("picks").select("id", { count: "exact", head: true })
      .eq("status", "published")
      .is("deleted_at", null)
      .gte("published_at", bounds.start)
      .lt("published_at", bounds.end);
    if (opts.top5Only) q = q.eq("is_top5", true);
    const { count } = await q;
    if ((count ?? 0) > 0) return bounds;
  }
  return bettingDayBounds();
}
